/**
 * <scroll-spy-nav>
 *
 * Wraps a sticky subnav ([data-subnav]) and a series of anchored sections.
 * Highlights the subnav link whose section is currently in view and
 * smooth-scrolls to sections on click.
 *
 * Expected markup:
 *   <scroll-spy-nav>
 *     <nav data-subnav>
 *       <a href="#section-id">...</a>
 *     </nav>
 *     <section id="section-id">...</section>
 *   </scroll-spy-nav>
 */
class ScrollSpyNav extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('[data-subnav]');
    if (!this.nav) return;

    this.links = Array.from(this.nav.querySelectorAll('a[href^="#"]'));
    this.sections = this.links
      .map((link) => ({
        link,
        el: document.getElementById(decodeURIComponent(link.hash.slice(1))),
      }))
      .filter((entry) => entry.el);

    if (!this.sections.length) return;

    this.currentLink = null;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.setNavHeight = this.setNavHeight.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.setNavHeight);
    this.nav.addEventListener('click', this.onClick);

    this.setNavHeight();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.setNavHeight);
  }

  setNavHeight() {
    document.documentElement.style.setProperty('--shop-all-subnav-height', `${this.nav.offsetHeight}px`);
    this.update();
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  update() {
    // The activation line sits just below the stuck subnav.
    const line = this.nav.getBoundingClientRect().bottom + 8;

    // Last section whose top has crossed the line wins; default to the first.
    let active = this.sections[0];
    for (const section of this.sections) {
      if (section.el.getBoundingClientRect().top <= line) active = section;
    }

    // At the very bottom of the page a short final section may never cross
    // the line — force-activate it.
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
      active = this.sections[this.sections.length - 1];
    }

    if (active.link === this.currentLink) return;
    this.currentLink = active.link;

    this.links.forEach((link) => link.removeAttribute('aria-current'));
    active.link.setAttribute('aria-current', 'true');

    // Keep the active link visible in the horizontally scrollable nav.
    const list = active.link.closest('ul') || this.nav;
    if (list.scrollWidth > list.clientWidth) {
      list.scrollTo({ left: active.link.offsetLeft - 16, behavior: 'smooth' });
    }
  }

  onClick(event) {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    target.scrollIntoView({ behavior, block: 'start' });
    history.replaceState(null, '', link.hash);
  }
}

if (!customElements.get('scroll-spy-nav')) {
  customElements.define('scroll-spy-nav', ScrollSpyNav);
}
